<template>
  <BaseDialog
    :visible="visible"
    @update:visible="(val) => emit('update:visible', val)"
    :width="isMobile ? '100vw' : '878.72px'"
    :margin="modalMargin"
    :height="isMobile ? '100vh' : '694px'"
    :fullscreen="isMobile"
    :style="{
      width: isMobile ? '100%' : '878.72px',
      height: isMobile ? '100vh' : '694px',
      borderRadius: isMobile ? '0' : '16px',
      overflow: 'hidden'
    }"
    :dialogClass="[
      isMobile ? 'mobile-dialog' : 'fixed-dialog',
      'm-0 p-0',
      dialogClass
    ]"
  >
    <template #header>
      <div
        class="flex items-center justify-between p-4 lg:px-8 lg:py-4 lg:pb-2"
      >
        <div class="flex gap-2 items-center">
          <div
            v-if="headerIcon"
            :class="[isMobile ? '!w-[30px] !h-[30px]' : '', headerIconClass]"
            v-html="headerIcon"
          ></div>
          <h1
            :class="[isMobile ? '!text-[20px]' : '', headerClass]"
            v-html="headerText"
          ></h1>
        </div>
        <!-- NOSONAR -->
        <div
          :aria-label="$t('accessibilityLabels.close')"
          tabindex="0"
          :autofocus="visible ? true : false"
          @keydown.enter="$emit('close')"
          @keydown.space="$emit('close')"
          class="flex items-center cursor-pointer"
          @click="$emit('close')"
          v-html="closeIcon"
        ></div></div
    ></template>
    <div class="bg-white flex-1 w-full overflow-y-auto px-4 sm:px-6 pb-24 sm:pb-8 lg:px-8 lg:pb-8 lg:pt-2">
      <div>
        <div class="border border-solid border-whiteBlue"></div>
      </div>

      <div class="flex items-center justify-center flex-col h-full">
        <div class="pb-[20px] flex justify-center items-center  lg:mt-6">
          <div class="flex items-center justify-center lg:mt-8">
            <div
              v-html="roundedShareSvg"
              class="flex items-center justify-center"
              :class="{ '!w-[50px] !h-[50px]': isMobile }"
              style="flex-shrink: 0"
            ></div>
          </div>
          <div
            v-html="dashedSvg({ width: isMobile ? '80' : '137' })"
            class="flex items-center justify-center lg:mt-8"
          ></div>
          <div
            class="bg-white rounded-[16px] w-full border border-solid border-lightGrayBorder font-just shadow-sm p-4 flex flex-col items-center mt-10 sm:mt-5 lg:mt-10"
          >
            <div class="flex items-center justify-center relative mb-2">
              <div class="relative">
                <img
                  :src="
                    item?.id
                      ? `${BaseURL}/objects/${item?.id}/thumbnail`
                      : defaultImage
                  "
                  alt="avatar"
                  class="w-[48px] h-[48px] object-cover rounded-full"
                  @error="(e) => (e.target.src = defaultImage)"
                />
                <img
                  v-if="item?.badgeIcon"
                  :src="item?.badgeIcon"
                  alt="icon"
                  class="w-[33px] h-[33px] absolute -right-6 top-1/2 -translate-y-1/2"
                />
              </div>
              <span
                class="ml-8 font-just font-[400] text-[12px] md:text-[16px] text-greyLight"
                v-if="item?.tag"
              >
                {{ $te(item.tag) ? $t(item.tag) : ($te(`${item.tag}.name`) ? $t(`${item.tag}.name`) : item.tag) }}
              </span>
            </div>
            <span
              class="font-just font-semibold text-[16px] md:text-[20px] text-darkBlue text-left w-full"
              v-if="item?.name"
            >
              {{ item?.name }}
            </span>
          </div>
        </div>

        <div class="text-center pb-6">
          <p class="font-just" :class="subtitleClass" v-html="subtitle"></p>
        </div>

        <div class="w-full px-2 sm:px-0">
          <BaseInput
            :required="true"
            type="email"
            v-model="form.email"
            :label="$t('sendRequestModal.enterTheirEmail')"
            class="w-full font-just my-2"
            :disabled="Boolean(item?.email)"
            name="email"
            id="email"
            :error-message="getErrorMessage('email')"
            :invalid="validation.email.$error"
            @blur="validation.email.$touch"
          />
          <BaseInput
            v-model="message"
            :textarea="true"
            :placeholder="$t('sendRequestModal.enterTheirMessage')"
            class="w-full font-just"
            :aria-label="$t('sendRequestModal.enterTheirMessage')"
          />
        </div>
      </div>
      <div v-if="errorMsg">
        <span
          class="text-base my-2 !font-just text-error flex justify-center"
          data-test-id="errorMessage"
        >
          {{ errorMsg }}
        </span>
      </div>
    </div>
    <template #footer>
      <div class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div class="p-2 mx-auto w-full p-4 max-w-3xl">
          <div
            class="flex flex-col sm:flex-row items-center justify-between gap-3 w-full !m-0 !mb-2"
            :class="btnSectionClass"
          >
        <BaseButton
          class="w-full"
          @click="$emit('submit', message, form.email)"
          :loading="isLoading"
          :disabled="isLoading || isSubmitDisabled"
          ><p>{{ buttonText }}</p>
          <div
            v-html="fastShareSvg"
            class="flex justify-center items-center"
          ></div
        ></BaseButton>

        <BaseButton
          v-if="showSkipButton"
          class="w-full"
          @click="handleSkipClick"
          :loading="isLoading"
          :disabled="isLoading || isSubmitDisabled"
        >
          <p>{{ $t('sendRequestModal.skipInvitation') }}</p>
          <div class="flex justify-center items-center"></div>
        </BaseButton>
          </div>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
import { dashedSvg, fastShareSvg, roundedShareSvg } from "@/utils/commonSvg";
import { validationMessages } from "@/utils/validation";
import { useI18n } from "vue-i18n";
import useVuelidate from "@vuelidate/core";
import {
  required,
  email as emailValidator,
  helpers,
} from "@vuelidate/validators";
import { BaseURL } from "@/api/apiService";
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { defaultImage } from "@/utils/common";
const { t } = useI18n();
const isMobile = ref(window.innerWidth < 640);

const modalMargin = computed(() => {
  const width = window.innerWidth;
  return width >= 640 && width <= 902 ? '20px' : '0';
});

// Handle window resize
const handleResize = () => {
  isMobile.value = window.innerWidth < 640;
  // Force dialog repositioning
  if (window.PrimeVue) {
    const dialog = document.querySelector('.p-dialog');
    if (dialog) {
      const instance = window.PrimeVue.Dialog.getInstance(dialog);
      if (instance) {
        instance.center();
      }
    }
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  // Initial check
  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

const props = defineProps({
  errorMsg: { type: String, default: "" },
  isLoading: { type: Boolean, default: false },
  btnSectionClass: String,
  headerIconClass: String,
  headerText: String,
  headerClass: String,
  subtitle: String,
  subtitleClass: String,
  section: { type: String, require: true },
  visible: { type: Boolean },
  type: { type: String, default: "" },
  item: { required: true },
  buttonText: { type: String, default: "Save and Send Request" },
  headerIcon: { type: String, required: true },
  closeIcon: { type: String, required: true },
  showSkipButton: { type: Boolean, default: false },
  dialogClass: {
    type: String,
    default:
      `bg-white rounded-2xl sm:rounded-2xl w-[95%] sm:w-[90%] md:w-[80%] lg:w-[878.72px] xl:w-[878.72px] max-w-[878.72px] !h-[694px] m-0 sm:m-4 overflow-hidden`,
  },
});

let message = ref("");
const skipInvite = ref("");
const form = reactive({
  email: props.item?.email || "",
});

const emit = defineEmits(["update:visible", "submit", "close"]);

// Watch for visibility changes
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      // Reset form when modal opens
      form.email = props.item?.email || "";
    }
  },
  { immediate: true }
);

// Watch for external item changes
watch(
  () => props.item,
  (newVal) => {
    form.email = newVal?.email || "";
  },
  { immediate: true }
);

const rules = {
  email: {
    required: helpers.withMessage(
      validationMessages.required(t("commonLabels.contactOptions.email")),
      required
    ),
    email: helpers.withMessage(
      validationMessages.format(t("commonLabels.contactOptions.email")),
      emailValidator
    ),
  },
};

// Form validation instance
const validation = useVuelidate(rules, form);

// Get error message for a field.Returns the first validation error if present, otherwise returns an empty string.
const getErrorMessage = (field) => {
  return validation.value[field].$error
    ? validation.value[field].$errors[0].$message
    : "";
};

const isSubmitDisabled = computed(() => {
  // If item already has email → no validation needed
  if (props.item?.email) return false;
  // Otherwise, disable if email invalid
  return validation.value.email.$invalid;
});

const handleSkipClick = () => {
  emit("submit", message.value, form.email, true);
};
</script>

<style scoped>
@media (max-width: 768px) {
  .text-center p.font-just {
    font-size: 20px;
  }
}
:deep(.fixed-dialog) {
  width: 878.72px !important;
  height: 694px !important;
  max-width: 878.72px !important;
  max-height: 694px !important;
  margin: 1rem auto !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

:deep(.p-dialog) {
  /* Mobile-first: fullscreen by default */
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100% !important;
  max-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
}

:deep(.p-dialog-content) {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  background: white !important;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
  padding-bottom: 100px !important; /* Space for fixed footer */
  height: auto !important;
  max-height: none !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
}

:deep(.p-dialog .p-dialog-content) {
  flex: 1;
  overflow-y: auto;
}

:deep(.p-dialog-header) {
  padding: 1rem !important;
  border-bottom: 1px solid #e5e7eb !important;
  margin: 0 !important;
  border: none !important;
}

:deep(.p-dialog-footer) {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  padding: 0.75rem 1rem !important;
  margin: 0 !important;
  border: none !important;
  border-top: 1px solid #e5e7eb !important;
  background: white !important;
  z-index: 1000 !important;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05) !important;
}

:deep(.p-dialog-mask) {
  background-color: rgba(0, 0, 0, 0.5) !important;
}
</style>
